import { useState, useEffect, useRef } from 'react';
import { companiesApi, meetingNotesApi, companyAssetsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const ASSET_TYPE_LABELS = {
  wishlist: 'Wishlist',
  cost_data: 'Cost Data',
  other: 'Other',
};

export default function CompanyDetail({ companyId, onBack }) {
  const { role } = useAuth();
  const [company, setCompany] = useState(null);
  const [notes, setNotes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('wishlist');
  const fileRef = useRef();

  const isIrRep = role === 'company' || role === 'admin';

  useEffect(() => {
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadCompany = async () => {
    try {
      const data = await companiesApi.get(companyId);
      setCompany(data.company);
      setNotes(data.meeting_notes || []);
      setAssets(data.assets || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await meetingNotesApi.create({
        company_id: companyId,
        note: newNote,
        meeting_date: noteDate || null,
      });
      setNewNote('');
      setNoteDate('');
      await loadCompany();
    } catch (err) {
      console.error(err);
    }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await meetingNotesApi.delete(noteId);
      await loadCompany();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await companyAssetsApi.upload(companyId, file, uploadType);
      }
      await loadCompany();
    } catch (err) {
      console.error(err);
    }
    setUploading(false);
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await companyAssetsApi.delete(companyId, assetId);
      await loadCompany();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;
  if (!company) return <div className="text-center py-16 text-gray-400">Company not found</div>;

  const wishlist = company.wishlist_survey || {};

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-vault-navy mb-4 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Matches
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <div className="flex items-center gap-4 mb-4">
              {company.logo_url ? (
                <img src={company.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-vault-navy/10 flex items-center justify-center text-vault-navy font-bold text-xl">
                  {company.name?.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-vault-navy">{company.name}</h1>
                {company.website && (
                  <p className="text-sm text-gray-400">{company.website}</p>
                )}
              </div>
            </div>
            <p className="text-gray-600">{company.description}</p>
          </div>

          {/* Wishlist */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-4">What They're Looking For</h2>

            {wishlist.summary && (
              <p className="text-gray-600 mb-4 p-3 bg-vault-surface rounded-lg text-sm italic">
                "{wishlist.summary}"
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              {company.categories_seeking?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Categories</div>
                  <div className="flex flex-wrap gap-1">
                    {company.categories_seeking.map(c => (
                      <span key={c} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full capitalize">
                        {c.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {company.age_ranges_seeking?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Age Ranges</div>
                  <div className="flex flex-wrap gap-1">
                    {company.age_ranges_seeking.map(a => (
                      <span key={a} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {company.price_points_seeking?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Price Points</div>
                  <div className="flex flex-wrap gap-1">
                    {company.price_points_seeking.map(p => (
                      <span key={p} className="text-xs px-2 py-0.5 bg-green-50 text-green-600 rounded-full capitalize">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {company.mechanics_seeking?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Mechanics</div>
                  <div className="flex flex-wrap gap-1">
                    {company.mechanics_seeking.map(m => (
                      <span key={m} className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full capitalize">
                        {m.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Company Files (IR reps only) */}
          {isIrRep && (
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-vault-navy">Company Files</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Private. Only you can see these files.</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="px-2 py-1.5 border border-vault-border rounded-lg text-xs bg-white"
                  >
                    <option value="wishlist">Wishlist PDF</option>
                    <option value="cost_data">Cost Data</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="px-3 py-1.5 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                    onChange={handleUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {assets.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <p className="text-sm">No files uploaded yet</p>
                  <p className="text-xs mt-1">Upload wishlist PDFs, cost data, or other confidential documents</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assets.map(asset => (
                    <div key={asset.id} className="flex items-center justify-between p-3 bg-vault-surface rounded-lg group">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">
                          {asset.file_type?.includes('pdf') ? '\uD83D\uDCC4' :
                           asset.file_type?.startsWith('image/') ? '\uD83D\uDDBC' : '\uD83D\uDCC1'}
                        </span>
                        <div>
                          <div className="text-sm font-medium text-vault-navy">{asset.file_name}</div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              asset.asset_type === 'wishlist' ? 'bg-red-100 text-red-600' :
                              asset.asset_type === 'cost_data' ? 'bg-blue-100 text-blue-600' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {ASSET_TYPE_LABELS[asset.asset_type]}
                            </span>
                            <span>{asset.file_size ? `${(asset.file_size / 1024).toFixed(0)} KB` : ''}</span>
                            <span>{new Date(asset.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="text-gray-300 hover:text-vault-error opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Meeting Notes sidebar */}
        <div>
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-4">Meeting Notes</h2>
            <p className="text-xs text-gray-400 mb-4">Private to you. These improve your match scores.</p>

            <form onSubmit={handleAddNote} className="mb-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note from a meeting or interaction..."
                rows={3}
                className="w-full px-3 py-2 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none mb-2"
              />
              <div className="flex gap-2">
                <input
                  type="date"
                  value={noteDate}
                  onChange={(e) => setNoteDate(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold"
                />
                <button
                  type="submit"
                  disabled={savingNote || !newNote.trim()}
                  className="px-3 py-1.5 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingNote ? '...' : 'Add'}
                </button>
              </div>
            </form>

            {notes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No notes yet</p>
            ) : (
              <div className="space-y-3">
                {notes.map(note => (
                  <div key={note.id} className="p-3 bg-vault-surface rounded-lg group">
                    <div className="flex items-start justify-between">
                      <p className="text-sm text-gray-600">{note.note}</p>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-gray-300 hover:text-vault-error opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0"
                      >
                        &times;
                      </button>
                    </div>
                    {note.meeting_date && (
                      <p className="text-xs text-gray-400 mt-1">{new Date(note.meeting_date).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
